import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Search,
  Download,
  AttachMoney,
  Receipt,
  People,
  DirectionsBus,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.112.10:8000/api';
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

export default function Reportes() {
  const [tipoReporte, setTipoReporte] = useState('recaudacion');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [datos, setDatos] = useState(null);

  const handleBuscar = async () => {
    if (!fechaDesde || !fechaHasta) {
      setError('Debes seleccionar ambas fechas');
      return;
    }

    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('admin_token');
      
      const response = await axios.get(`${API_URL}/dashboard/reportes`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          tipo: tipoReporte,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
        },
      });
      
      setDatos(response.data);
    } catch (error) {
      console.error('Error al generar reporte:', error);
      setError('Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  };

  const formatMonto = (monto) => {
    return `Gs. ${parseFloat(monto || 0).toLocaleString('es-PY')}`;
  };

  const renderReporteRecaudacion = () => {
    if (!datos) return null;

    return (
      <Box>
        {/* Resumen */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6}>
            <Card sx={{ bgcolor: 'primary.light' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Receipt sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">
                    Total Boletas
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {datos.total_boletas?.toLocaleString('es-PY') || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Card sx={{ bgcolor: 'success.light' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoney sx={{ mr: 1 }} />
                  <Typography variant="subtitle2">
                    Total Recaudado
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {formatMonto(datos.total_recaudado)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Gráfico por Día */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Recaudación por Día
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={datos.por_dia || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="fecha" />
              <YAxis />
              <Tooltip formatter={(value) => formatMonto(value)} />
              <Legend />
              <Line type="monotone" dataKey="total" stroke="#8884d8" name="Monto" />
              <Line type="monotone" dataKey="cantidad" stroke="#82ca9d" name="Cantidad" />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Gráfico por Tarifa */}
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Distribución por Tarifa
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={datos.por_tarifa || []}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={(entry) => `${entry.tarifa?.nombre}: ${formatMonto(entry.total)}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="total"
              >
                {(datos.por_tarifa || []).map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip formatter={(value) => formatMonto(value)} />
            </PieChart>
          </ResponsiveContainer>
        </Paper>
      </Box>
    );
  };

  const renderReporteConductores = () => {
    if (!datos) return null;

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Top Conductores por Recaudación
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={datos || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip formatter={(value) => formatMonto(value)} />
              <Legend />
              <Bar dataKey="total_recaudado" fill="#8884d8" name="Recaudación" />
              <Bar dataKey="total_boletas" fill="#82ca9d" name="Boletas" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Conductor</TableCell>
                <TableCell>Email</TableCell>
                <TableCell align="right">Cajas</TableCell>
                <TableCell align="right">Boletas</TableCell>
                <TableCell align="right">Recaudación</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(datos || []).map((conductor) => (
                <TableRow key={conductor.id}>
                  <TableCell>{conductor.nombre}</TableCell>
                  <TableCell>{conductor.email}</TableCell>
                  <TableCell align="right">{conductor.total_cajas}</TableCell>
                  <TableCell align="right">{conductor.total_boletas}</TableCell>
                  <TableCell align="right">{formatMonto(conductor.total_recaudado)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderReporteTarifas = () => {
    if (!datos) return null;

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Uso de Tarifas
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={datos || []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="nombre" />
              <YAxis />
              <Tooltip formatter={(value, name) => name === 'total_recaudado' ? formatMonto(value) : value} />
              <Legend />
              <Bar dataKey="total_boletas" fill="#8884d8" name="Boletas Vendidas" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tarifa</TableCell>
                <TableCell>Monto Unitario</TableCell>
                <TableCell align="right">Boletas Vendidas</TableCell>
                <TableCell align="right">Recaudación Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(datos || []).map((tarifa) => (
                <TableRow key={tarifa.id}>
                  <TableCell>{tarifa.nombre}</TableCell>
                  <TableCell>{formatMonto(tarifa.monto)}</TableCell>
                  <TableCell align="right">{tarifa.total_boletas || 0}</TableCell>
                  <TableCell align="right">{formatMonto(tarifa.total_recaudado)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const renderReporteRendiciones = () => {
    if (!datos) return null;

    return (
      <Box>
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Rendiciones
                </Typography>
                <Typography variant="h4">
                  {datos.total_rendiciones}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card sx={{ bgcolor: 'warning.light' }}>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Con Diferencia
                </Typography>
                <Typography variant="h4">
                  {datos.con_diferencia}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Declarado
                </Typography>
                <Typography variant="h6">
                  {formatMonto(datos.total_declarado)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="text.secondary">
                  Total Calculado
                </Typography>
                <Typography variant="h6">
                  {formatMonto(datos.total_calculado)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Conductor</TableCell>
                <TableCell>Rendidor</TableCell>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Declarado</TableCell>
                <TableCell align="right">Calculado</TableCell>
                <TableCell align="right">Diferencia</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(datos.rendiciones || []).map((rendicion) => (
                <TableRow key={rendicion.id}>
                  <TableCell>{rendicion.id}</TableCell>
                  <TableCell>{rendicion.conductor}</TableCell>
                  <TableCell>{rendicion.rendidor}</TableCell>
                  <TableCell>{new Date(rendicion.fecha_rendicion).toLocaleDateString('es-PY')}</TableCell>
                  <TableCell align="right">{formatMonto(rendicion.monto_declarado)}</TableCell>
                  <TableCell align="right">{formatMonto(rendicion.monto_calculado)}</TableCell>
                  <TableCell align="right" sx={{ color: rendicion.tiene_diferencia ? 'error.main' : 'success.main' }}>
                    {formatMonto(rendicion.diferencia)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Reportes y Estadísticas
      </Typography>

      {/* Filtros */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Tipo de Reporte</InputLabel>
              <Select
                value={tipoReporte}
                label="Tipo de Reporte"
                onChange={(e) => setTipoReporte(e.target.value)}
              >
                <MenuItem value="recaudacion">Recaudación</MenuItem>
                <MenuItem value="conductores">Conductores</MenuItem>
                <MenuItem value="tarifas">Tarifas</MenuItem>
                <MenuItem value="rendiciones">Rendiciones</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fecha Desde"
              type="date"
              value={fechaDesde}
              onChange={(e) => setFechaDesde(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Fecha Hasta"
              type="date"
              value={fechaHasta}
              onChange={(e) => setFechaHasta(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <Button
              variant="contained"
              fullWidth
              startIcon={loading ? <CircularProgress size={20} /> : <Search />}
              onClick={handleBuscar}
              disabled={loading}
            >
              Generar Reporte
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Resultados */}
      {datos && (
        <Box>
          {tipoReporte === 'recaudacion' && renderReporteRecaudacion()}
          {tipoReporte === 'conductores' && renderReporteConductores()}
          {tipoReporte === 'tarifas' && renderReporteTarifas()}
          {tipoReporte === 'rendiciones' && renderReporteRendiciones()}
        </Box>
      )}

      {!datos && !loading && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            Selecciona un tipo de reporte y fechas, luego presiona "Generar Reporte"
          </Typography>
        </Paper>
      )}
    </Box>
  );
}
